import React from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import { maskBinSuggestsTroy, normalizeCardBrandKey } from "../cardBrand";
import CardBrandIcon from "./CardBrandIcon";

/** base CardContent style — 2×2 grid like reference layout (row 1: information | row 2: bank | default) */
export const savedPaymentMethodRowContentSx = {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    gridTemplateRows: "auto auto",
    alignItems: "start",
    columnGap: 2,
    rowGap: 0.75,
    py: 2,
    px: 2,
    "&:last-child": { pb: 2 },
};

function RowTopLine({ method }) {
    const { t } = useTranslation();
    const last4 = method.card_last_four;
    const brand = (method.card_brand || "").trim();
    const brandKey = normalizeCardBrandKey(brand);
    const iconBrand = maskBinSuggestsTroy(method.mask_bin) ? "troy" : brandKey;
    const labelText = (method.label || "").trim();
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 1.25,
                flexWrap: "nowrap",
                minWidth: 0,
            }}
        >
            <Typography
                variant="subtitle2"
                fontWeight={700}
                noWrap
                sx={{
                    maxWidth: "42%",
                    color: labelText ? "text.primary" : "text.disabled",
                }}
            >
                {labelText || "—"}
            </Typography>
            {iconBrand ? (
                <CardBrandIcon brand={iconBrand} size="medium" />
            ) : brand ? (
                <Chip label={brand} size="small" variant="outlined" sx={{ fontWeight: 600, flexShrink: 0 }} />
            ) : null}
            {last4 ? (
                <Typography variant="subtitle2" component="span" fontWeight={600} sx={{ flexShrink: 0, color: "text.primary" }}>
                    •••• {last4}
                </Typography>
            ) : (
                <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
                    {method.mask_bin || t("paramPayCard")}
                </Typography>
            )}
        </Box>
    );
}

function RowBank({ method }) {
    const bankName = (method.bank_name || "").trim();
    if (!bankName) {
        return null;
    }
    return (
        <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            sx={{
                pl: 0.25,
                display: "block",
                textTransform: "uppercase",
                letterSpacing: "0.02em",
                fontWeight: 500,
            }}
        >
            {bankName}
        </Typography>
    );
}

/**
* one saved card row — layout like reference layout (two rows, actions right)
 */
function SavedPaymentMethodRow({
    method,
    selected = false,
    onRowClick,
    onRemoveCard,
    onDefaultChange,
    defaultUpdatingId = null,
    cardSx = {},
    cardContentSx = {},
}) {
    const { t } = useTranslation();
    const theme = useTheme();
    const showDelete = typeof onRemoveCard === "function";
    const showDefault = typeof onDefaultChange === "function";
    const hasRightCol = showDelete || showDefault;
    const interactive = typeof onRowClick === "function";
    const bankName = (method.bank_name || "").trim();

    const handleKeyDown = (e) => {
        if (!interactive) {
            return;
        }
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onRowClick();
        }
    };

    const stop = (e) => e.stopPropagation();

    const cardBorder = selected ? theme.palette.primary.main : theme.palette.divider;

    const contentGridSx = hasRightCol
        ? { ...savedPaymentMethodRowContentSx, ...cardContentSx }
        : {
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              gap: 0.75,
              py: 2,
              px: 2,
              "&:last-child": { pb: 2 },
              ...cardContentSx,
          };

    return (
        <Card
            variant="outlined"
            sx={{
                mb: 1,
                cursor: interactive ? "pointer" : "default",
                borderRadius: "12px",
                borderWidth: 1,
                borderStyle: "solid",
                borderColor: cardBorder,
                bgcolor: "background.paper",
                boxShadow: "none",
                ...cardSx,
            }}
            onClick={interactive ? onRowClick : undefined}
            role={interactive ? "button" : undefined}
            tabIndex={interactive ? 0 : undefined}
            onKeyDown={handleKeyDown}
        >
            <CardContent sx={contentGridSx}>
                {hasRightCol ? (
                    <>
                        <Box sx={{ gridColumn: 1, gridRow: 1, minWidth: 0, alignSelf: "center" }}>
                            <RowTopLine method={method} />
                        </Box>
                        <Box
                            sx={{
                                gridColumn: 1,
                                gridRow: 2,
                                minWidth: 0,
                                alignSelf: "center",
                                minHeight: showDefault && !bankName ? 32 : undefined,
                            }}
                        >
                            <RowBank method={method} />
                        </Box>
                        {showDelete ? (
                            <Box
                                sx={{ gridColumn: 2, gridRow: 1, justifySelf: "end", alignSelf: "start", lineHeight: 0 }}
                                onClick={stop}
                            >
                                <IconButton
                                    size="small"
                                    sx={{ flexShrink: 0, p: 0.5, color: "text.secondary" }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onRemoveCard(method.id);
                                    }}
                                >
                                    <DeleteOutline fontSize="small" />
                                </IconButton>
                            </Box>
                        ) : null}
                        {showDefault ? (
                            <Box
                                sx={{
                                    gridColumn: 2,
                                    gridRow: 2,
                                    justifySelf: "end",
                                    alignSelf: "center",
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                    gap: 0.75,
                                    flexShrink: 0,
                                }}
                                onClick={stop}
                            >
                                <Typography
                                    variant="caption"
                                    component="span"
                                    color="text.primary"
                                    fontWeight={600}
                                    sx={{ lineHeight: 1.25, whiteSpace: "nowrap" }}
                                >
                                    {t("paramPayCardDefaultPay")}
                                </Typography>
                                <Switch
                                    size="small"
                                    sx={{ flexShrink: 0 }}
                                    checked={Boolean(method.is_default)}
                                    disabled={defaultUpdatingId === method.id}
                                    onChange={(e) => onDefaultChange(method.id, e.target.checked)}
                                    inputProps={{
                                        "aria-label": t("paramPayCardDefaultPay"),
                                    }}
                                />
                            </Box>
                        ) : null}
                    </>
                ) : (
                    <>
                        <RowTopLine method={method} />
                        <RowBank method={method} />
                    </>
                )}
            </CardContent>
        </Card>
    );
}

export default SavedPaymentMethodRow;
